import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useGrowl } from "PFApp/use_growl";
import { fetchFeatures, toggleFeature } from "PFCore/services/features";
import { Collection } from "PFTypes";
import { Feature } from "PFTypes/features";
import { QueryOptions } from "PFTypes/request_options";

import { queryKeys } from "./query_keys";

type UseFeatures = {
  accountId: number;
  currentPage: number;
  options?: QueryOptions<Collection<Feature[]>>;
};

type UseFeaturesReturn = {
  features: Feature[];
  totalPages: number;
  isLoading: boolean;
  error: AxiosError | null;
};

export const useFeatures = ({ accountId, currentPage, options }: UseFeatures): UseFeaturesReturn => {
  const growl = useGrowl();
  const { data, isLoading, error } = useQuery<Collection<Feature[]>, AxiosError>(
    queryKeys.features(accountId, currentPage),
    async () => await fetchFeatures(accountId, currentPage, PER_PAGE_UNPAGINATED),
    {
      ...options,
      onError: (error: AxiosError) => {
        growl({
          message: error.message,
          kind: "error"
        });
      }
    }
  );

  return {
    features: (data?.entries ?? []) as Feature[],
    totalPages: 1,
    isLoading,
    error
  };
};

type UseToggleFeatures = {
  accountId: number;
  currentPage: number;
};

export const useToggleFeatures = ({ accountId, currentPage }: UseToggleFeatures) => {
  const queryClient = useQueryClient();
  const growl = useGrowl();
  return useMutation((feature: Feature) => toggleFeature(feature, accountId), {
    onSuccess: () => queryClient.invalidateQueries(queryKeys.features(accountId, currentPage)),
    onError: (error: AxiosError) => {
      growl({
        message: error.message,
        kind: "error"
      });
    }
  });
};
